import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Building2, 
  Plus,
  Loader2,
  Pencil,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function GerenciasPage() {
  const { user } = useAuth();
  const [gerencias, setGerencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGerencia, setEditingGerencia] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchGerencias();
  }, []);

  const fetchGerencias = async () => {
    try {
      const response = await axios.get(`${API_URL}/gerencias`);
      setGerencias(response.data);
    } catch (error) {
      toast.error('Error al cargar gerencias/áreas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (gerencia = null) => {
    if (gerencia) {
      setEditingGerencia(gerencia);
      setForm({
        name: gerencia.name,
        description: gerencia.description || ''
      });
    } else {
      setEditingGerencia(null);
      setForm({ name: '', description: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingGerencia(null);
    setForm({ name: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    
    try {
      if (editingGerencia) {
        await axios.patch(`${API_URL}/gerencias/${editingGerencia.id}`, form);
        toast.success('Gerencia/Área actualizada');
      } else {
        await axios.post(`${API_URL}/gerencias`, form);
        toast.success('Gerencia/Área creada');
      }
      handleCloseDialog();
      fetchGerencias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (gerenciaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta gerencia/área?')) return;
    
    try {
      await axios.delete(`${API_URL}/gerencias/${gerenciaId}`);
      toast.success('Gerencia/Área eliminada');
      fetchGerencias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7FD02E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gerencias-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-800">Gerencias / Áreas</h1>
          <p className="text-gray-500">Administra las gerencias y áreas de tu organización</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#7FD02E] hover:bg-[#6BC024] text-white shadow-sm" 
              onClick={() => handleOpenDialog()}
              data-testid="new-gerencia-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Gerencia/Área
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle className="text-gray-800">
                  {editingGerencia ? 'Editar Gerencia/Área' : 'Nueva Gerencia/Área'}
                </DialogTitle>
                <DialogDescription className="text-gray-500">
                  {editingGerencia ? 'Modifica los datos de la gerencia/área' : 'Agrega una nueva gerencia o área a tu organización'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700">Nombre *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ej: Gerencia de TI"
                    required
                    className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                    data-testid="gerencia-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-gray-700">Descripción</Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Descripción opcional..."
                    rows={3}
                    className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                    data-testid="gerencia-description-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={handleCloseDialog} className="text-gray-600">
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={actionLoading || !form.name.trim()} 
                  className="bg-[#7FD02E] hover:bg-[#6BC024] text-white"
                  data-testid="submit-gerencia-btn"
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingGerencia ? 'Guardar' : 'Crear'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <Card className="border-gray-200 bg-white shadow-sm" data-testid="gerencias-table">
        <CardContent className="p-0">
          {gerencias.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-800">No hay gerencias/áreas</p>
              <p className="text-gray-500">Crea una nueva para comenzar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-100 hover:bg-transparent">
                  <TableHead className="text-gray-500">Nombre</TableHead>
                  <TableHead className="text-gray-500">Descripción</TableHead>
                  <TableHead className="text-gray-500 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gerencias.map((gerencia) => (
                  <TableRow key={gerencia.id} className="border-gray-100" data-testid={`gerencia-row-${gerencia.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#7FD02E]/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-[#7FD02E]" />
                        </div>
                        <span className="font-medium text-gray-800">{gerencia.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {gerencia.description || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(gerencia)}
                          className="text-gray-400 hover:text-[#7FD02E]"
                          data-testid={`edit-gerencia-${gerencia.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(gerencia.id)}
                          className="text-gray-400 hover:text-destructive"
                          data-testid={`delete-gerencia-${gerencia.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
